import queryClient from "api/queryClient.ts";
import { useDestroyScenarioMutation, useGetScenarioQuery } from "api/TenantSlug/ConnectionCreator/hooks";
import { ScenarioGeneralForm } from "components/Forms/ScenarioGeneralForm/ScenarioGeneralForm.tsx";
import { DeleteModal } from "components/other/DeleteModal/DeleteModal.tsx";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useTenantNavigate } from "hooks/useTenantNavigate.ts";
import { useState } from "react";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums.ts";
import { FormId } from "types/models.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { getToolValidationFieldHeaderItems } from "utils/tenantHeaderItems.ts";

export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/validation/_layout/$scenarioId/_layout/general/"
)({
    component: ValidationGeneralPage,
    staticData: {
        permissions: [PermissionEnum.ToolScenarioFieldGeneral],
        showInSideMenu: false,
        label: "pages.tool.scenario.validation.general.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.scenario.validation.general.title"),
        };
    },
});

function ValidationGeneralPage() {
    const { scenarioId } = Route.useParams();
    const { data, isLoading, isError } = useGetScenarioQuery(scenarioId, {
        filter: { include: "formStructures" },
    });
    const scenario = data?.scenario;
    const formId = FormId.ScenarioGeneralForm;
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const permissionsUtils = usePermission();
    const tenantNavigate = useTenantNavigate();
    const { t } = usePaldockTranslation("forms.scenario-general");

    const destroyScenarioMutation = useDestroyScenarioMutation(scenarioId, {
        onSuccess: () => {
            toast.success(t("messages.delete_success"));
            queryClient.invalidateQueries({ queryKey: ["getScenarios"] });
            tenantNavigate("/tool/validation");
        },
        onError: () => {
            toast.error(t("messages.delete_error"));
            setDeleteModalOpen(false);
        },
    });

    if (!scenario) {
        return <QueryState isLoading={isLoading} isError={isError || !isLoading} errorKey="errors.loading_scenario" />;
    }

    const canDelete = permissionsUtils.hasPermission(PermissionEnum.ConnectionCreatorScenarioDelete);

    return (
        <TenantPage
            headerNavigationItems={getToolValidationFieldHeaderItems}
            saveFormId={formId}
            isEdit
            onDelete={canDelete ? () => setDeleteModalOpen(true) : null}
            deleteLoading={destroyScenarioMutation.isPending}
        >
            <ScenarioGeneralForm key={scenarioId} scenario={scenario} />
            <DeleteModal
                open={deleteModalOpen}
                title={scenario.title}
                onCancel={() => setDeleteModalOpen(false)}
                onConfirm={() => destroyScenarioMutation.mutate()}
                loading={destroyScenarioMutation.isPending}
            />
        </TenantPage>
    );
}
